import { useEffect, useRef, useState } from 'react';
import { Flex, Spin } from 'antd';
import { Navigate, Outlet, useLocation, useSearchParams } from 'react-router';
import { primeiroToque } from '@titularis/analytics';
import { ApiError } from '@titularis/api-client';
import { api } from './api';
import { identificar, registrarEvento } from './analytics';
import { useSession } from './auth';
import { destinoSeguro } from './pages/auth/destino';

/**
 * Diz ao analytics quem é esta pessoa, uma vez por conta.
 *
 * **Duas coisas acontecem aqui, e as duas faltavam.** A primeira é o
 * `identificar`: ele existia desde o PR do PostHog e nunca era chamado, então
 * toda a navegação do aluno era anônima e não havia como ligar uma compra a uma
 * pessoa. A segunda é a atribuição: é neste ponto - dentro do app, com sessão -
 * que o canal guardado no cookie da landing finalmente tem uma conta a que se
 * prender.
 *
 * **Fica no guarda, e não na tela de login**, porque metade das contas entra
 * pelo Google e volta direto de um redirecionamento, sem passar por formulário
 * nenhum. Aqui passa todo mundo que está dentro.
 *
 * O `ref` guarda o id já tratado: o guarda remonta a cada troca de rota, e sem
 * ele a chamada sairia a cada navegação.
 *
 * **A falha continua não derrubando nada, e passa a aparecer.** A origem é um
 * brinde: derrubar a navegação por ela seria trocar a conta pelo relatório. Mas
 * engolir o erro em silêncio é o que fez esta chamada responder 422 para todo
 * mundo entre 13 e 18/09/2026 - a rota estava montada antes do `express.json()`
 * e ninguém soube, porque não havia o que soubesse. O `attribution_failed` é o
 * que transforma a próxima quebra num número em vez de um silêncio.
 */
function descreverFalha(erro: unknown): string {
  // `status:code` é o que distingue as três causas possíveis: 422 é contrato
  // quebrado, 401 é sessão que evaporou, e 0 é rede ou CORS.
  if (erro instanceof ApiError) return `${erro.status}:${erro.code}`;
  return 'desconhecida';
}

function useIdentificacaoNoAnalytics(usuario: { id: string; email: string } | undefined): void {
  const tratado = useRef<string | null>(null);

  useEffect(() => {
    if (!usuario || tratado.current === usuario.id) return;
    tratado.current = usuario.id;

    identificar({ id: usuario.id, email: usuario.email });
    void api.registrarAtribuicao(primeiroToque()).catch((erro: unknown) => {
      registrarEvento({ nome: 'attribution_failed', motivo: descreverFalha(erro) });
    });
  }, [usuario]);
}

/**
 * A sessão, com a espera só na primeira vez.
 *
 * **O `isPending` do BetterAuth não quer dizer "ainda não sei quem é": ele
 * também sobe em toda revalidação de quem não tem sessão.** No `client/query.mjs`
 * da versão 1.6.24, o `onRequest` grava `isPending: currentValue.data === null` -
 * e o `signUp` dispara exatamente essa revalidação. O guarda, que trocava a tela
 * por um spinner sempre que `isPending` era verdadeiro, desmontava a página de
 * cadastro no instante seguinte ao cadastro: medido em 17/09/2026, a
 * confirmação "Confirme seu e-mail" apareceu em t=16989 ms, sumiu 5 ms depois e
 * o formulário voltou vazio em t=17052 ms.
 *
 * O trinco resolve: depois que a sessão se resolve **uma vez**, revalidação
 * nenhuma volta a apagar a tela. Quem não tem sessão continua sendo mandado
 * para o login - o que muda é só o intervalo em branco no meio do caminho.
 */
function useSessao() {
  const { data, isPending } = useSession();

  /*
   * Ajuste de estado **durante o render**, que é o que o React recomenda no
   * lugar de um efeito que só faz `setState` - e o que o lint desta casa exige
   * (`react-hooks/set-state-in-effect`). O React reexecuta o componente na hora,
   * antes de pintar, então não há render extra visível.
   */
  const [jaResolveu, setJaResolveu] = useState(false);
  if (!isPending && !jaResolveu) setJaResolveu(true);

  return { usuario: data?.user, esperando: isPending && !jaResolveu };
}

/**
 * Protege as rotas do aluno. A sessão é a do BetterAuth, em cookie compartilhado
 * com o admin. Isto é conveniência de navegação: quem autoriza de verdade é o
 * backend, em toda requisição (G-POLICY).
 */
export function RequireAuth() {
  const { usuario, esperando } = useSessao();
  const location = useLocation();

  useIdentificacaoNoAnalytics(usuario);

  if (esperando) {
    return (
      <Flex align="center" justify="center" style={{ minHeight: '100dvh' }}>
        <Spin />
      </Flex>
    );
  }

  if (!usuario) {
    /*
     * O destino vai no endereço, e não só no `state` do router: a landing mora
     * em outro domínio e não tem como escrever estado do React Router. Levando
     * o caminho pedido, quem clicou em "Comprar" na landing volta para os
     * planos assim que entrar, em vez de cair na vitrine.
     */
    const destino = `${location.pathname}${location.search}`;
    return (
      <Navigate
        to={`/entrar?destino=${encodeURIComponent(destino)}`}
        replace
        state={{ from: location.pathname }}
      />
    );
  }

  return <Outlet />;
}

/** Impede que quem já entrou volte às telas de login e cadastro. */
export function RedirectIfAuthenticated() {
  const { usuario, esperando } = useSessao();
  const [busca] = useSearchParams();

  if (esperando) {
    return (
      <Flex align="center" justify="center" style={{ minHeight: '100dvh' }}>
        <Spin />
      </Flex>
    );
  }

  /*
   * Também respeita o destino: quem já está logado e clica em "Comprar" na
   * landing chega em `/entrar?destino=/planos` e tem de ir para os planos, não
   * para a Home - senão o botão da landing não faz nada para quem já tem conta.
   */
  return usuario ? <Navigate to={destinoSeguro(busca.get('destino'))} replace /> : <Outlet />;
}
